/**
 * @file src/ui/react/studio/CardDrawer.tsx
 * @description The arrange drawer for the multi-stage Orbiter Studio — a bottom, resizable rail of the
 * collection's entries as draggable cards. A FAITHFUL PORT of root's mixed-collection `card-drawer.tsx`:
 * the same resizable rail (default 320 / min 152 / max 512), the same `cardHeight`-driven compact/reduced
 * card tiers, the same scroll arrows + end drop-zone. The RAIL CONTAINER is bespoke in root too (a fixed-
 * overlay `Drawer`/`Sheet` can't be an in-flow resizable rail that reflows the stages). Each card is the
 * ported `StationTrackCard` inside a draggable wrapper; the parent (`MultiStageStudio`) owns the pointer
 * drag (activation distance, overlay, stage/card hit-test, drop). Custom positioning/sizing is inline
 * `style`, because orbiters ships the design-lib's PRE-COMPILED Tailwind (`styles.css`) and has no Tailwind
 * pass of its own — so arbitrary utility classes would not exist.
 *
 * Visibility is controlled by the parent (the nav's "Cards" toggle); the drawer stays MOUNTED when closed
 * (collapsed to 0 height) so its resized height survives a toggle. Shown to EVERYONE who can view.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  FilePlus,
  FolderMinus,
  FolderSearch,
  HardDrive,
  Layers,
  LockKeyhole,
  MapPin,
  Route,
  ShipWheel,
  SlidersHorizontal,
  SquareArrowOutUpRight,
  Trash2,
  Waves,
} from 'lucide-react';
import {
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
  FourCornerCard,
} from 'plantasia.space-design/react';
import { Icon } from 'plantasia.space-design/icons';
import { StationTrackCard, type StationCardEntry } from './StationTrackCard';
import './cardDealFlip.css';

/* Playing a card is three beats: it turns face-down, is held there long enough to be seen, then
 * leaves the way it arrived — out to the right. Split out so each beat can be tuned on its own;
 * `HAND_CARD_EXIT_MS` is their sum and is what the rail waits before dropping the card, so the
 * card can never be unmounted mid-animation or linger after it. */
const HAND_CARD_TURN_MS = 460;
const HAND_CARD_HOLD_MS = 140;
const HAND_CARD_SLIDE_MS = 360;
/* The row closing up behind the card. Deliberately LONGER and softer than the card's own exit: the
 * card leaving is a gesture and wants to accelerate away, while the cards that remain are just
 * settling, and settling is the thing that looks wrong when it snaps. They start together. */
const HAND_CARD_CLOSE_MS = 480;
export const HAND_CARD_EXIT_MS =
  HAND_CARD_TURN_MS + HAND_CARD_HOLD_MS + Math.max(HAND_CARD_SLIDE_MS, HAND_CARD_CLOSE_MS);
import { entityMeta, entityPageUrl, type EntityRef } from './entityMeta';
import { dragCarriesFiles, type LocalCardAction, type LocalRailProps } from './localRail';
import { getT } from '../../../i18n/index.js';
// The app-wide control-tooltips system (the same flag the orbiter More menu toggles): the drawer
// offers a redundant toggle — assistance close at hand, whatever surface the user is on.
import { refreshControlTooltips, toggleControlTooltips } from '../../controlTooltips.js';
import { useTooltipsEnabled } from '../regions/useControlTooltips';

/** Run a toolbar toggle, then rebuild the control tooltips: the system resolves a tooltip's text
 *  when it builds the instance, so a state flip that rewrites the button's aria-label must rebuild
 *  for the next hover to tell the NEW state (the rebuild also retires the currently-open tooltip).
 *  setTimeout (not requestAnimationFrame) so the refresh runs after React commits the new label
 *  even in a hidden/backgrounded tab, where rAF can be deferred indefinitely. */
function toggleThenRefreshTooltips(toggle: () => void): void {
  toggle();
  setTimeout(() => refreshControlTooltips(), 0);
}

// Root's drawer sizing (card-drawer.tsx). The card height is derived from the drawer height, and drives
// the compact/reduced tiers — so the prominent square-art card appears as the drawer is dragged taller.
/** Exported so the studio can state the same number when it reports a drawer nobody has resized. */
export const DEFAULT_HEIGHT = 320;
const DESKTOP_MAX_HEIGHT = 512;
const MIN_CARD_HEIGHT = 88;
const DRAWER_CHROME_HEIGHT = 64;
const MIN_HEIGHT = MIN_CARD_HEIGHT + DRAWER_CHROME_HEIGHT; // 152
const CARD_VERTICAL_CLEARANCE = 76; // count row + card padding subtracted from content height
const CLOSE_THRESHOLD = 96; // drag the handle this far below MIN to close
const COMPACT_CARD_BREAKPOINT = 285;
const REDUCED_CARD_BREAKPOINT = 190;
const CARD_WIDTH = 300;
/** The rail's gap between cards. Shared with the leave animation, which has to close the card's
 *  gap as well as its width or the row keeps a 16px hole where the card was. */
const RAIL_GAP = 16;

/** A drawer card = the card's display fields (`StationCardEntry`), the entity it stands for (`EntityRef`
 *  — the ids the realm boots from, and what the card's context menu opens), plus the stable per-entry
 *  `voiceId`. The collection/queue data services build exactly this shape. */
export interface DrawerEntry extends StationCardEntry, EntityRef {
  voiceId: string;
  /** The COLLECTION ITEM id behind this card, when it came from a collection document. Distinct from
   *  `voiceId`, which falls back to an entity id. */
  itemId?: string | null;
  /** Whether THIS card can be taken out of whatever list it belongs to (a collection, or the user's
   *  queue) — the app decides, since only it knows which list this is and what the viewer may do.
   *  False/absent = the card is offered no removal, rather than one that could only ever fail. */
  removable?: boolean;
}

export interface CardDrawerProps {
  /** Open/closed is owned by the parent (the nav's "Cards" toggle). The drawer stays MOUNTED when closed
   *  (collapsed to 0 height) so its resized height survives a toggle. */
  open: boolean;
  /** Fix the drawer to this exact height and DROP the resize handle — used on mobile, where the drawer
   *  reflows the orbiter (shrinks the stage) instead of overlaying it, so it stays at one reduced height
   *  and is opened/closed only by the bar's Cards toggle. When omitted the drawer is resizable (desktop). */
  fixedHeight?: number;
  /** The height to open at — the one this viewer last left. Read ONCE, at mount: the drawer owns its
   *  height from then on, and a prop that kept overriding it would fight the handle under the finger.
   *  Absent/unusable falls back to the default, and anything out of range is clamped. */
  initialHeight?: number | null;
  /** The height SETTLED — a resize drag ended somewhere new. Only the settled value: a drag is a burst
   *  of frames, and the parent's job is to remember where it stopped, not to watch it move. */
  onHeightSettled?: (height: number) => void;
  entries: DrawerEntry[];
  activeVoiceId?: string | null;
  /** The entry currently being dragged (its card dims) — null when idle. */
  draggingVoiceId?: string | null;
  /** Reorder drop hints while dragging: insert-before this card, or at the rail end. */
  dropBeforeVoiceId?: string | null;
  dropAtEnd?: boolean;
  onCardPointerDown: (entry: DrawerEntry, event: ReactPointerEvent) => void;
  /** A card's context menu opened (right-click, or long-press on touch) — the parent drops any drag the
   *  same press had armed, so the gesture can't still complete behind the open menu. */
  onCardMenuOpen?: () => void;
  /** Remove a card from the list behind this session (a collection, or the user's queue), by its
   *  source voiceId. Absent/null and the card menu offers no removal at all. */
  onRemoveEntry?: ((sourceVoiceId: string) => void) | null;
  /** i18n key for the remove action's label — the card leaves a COLLECTION or a QUEUE, and each is its
   *  own whole phrase rather than a noun interpolated into one sentence. */
  removeEntryLabelKey?: string;
  /** Register each card's element (and the end zone) so the parent can hit-test reorder targets. */
  registerCardRef: (voiceId: string, node: HTMLElement | null) => void;
  registerEndRef: (node: HTMLElement | null) => void;
  /** Register the horizontal scroll container so the parent can drive the carousel scroll itself during a
   *  touch swipe (the cards are `touch-action: none`, so the rail no longer scrolls natively). */
  registerScrollRef: (node: HTMLElement | null) => void;
  /** Ref-callback for the drawer's OUTER container — the parent attaches `applyStudioChromeTheme`
   *  here so the drawer + its cards follow the USER chrome theme (the drawer is chrome, like the inspector).
   *  A React-19 ref callback that may return a cleanup. */
  containerRef?: (node: HTMLElement | null) => void | (() => void);
  /** Drag the resize handle far down to close — the parent owns the open/closed state. */
  onRequestClose: () => void;
  /** Open the local-files panel. Absent = the drawer offers no way in, which is how a surface that
   *  does not exist on this platform is declined — never a button that does nothing. */
  onOpenLocalFiles?: (() => void) | null;
  /** A drag out of the local-files panel is over the rail: same outline an OS file drag paints. */
  rowsDraggingOver?: boolean;
  /** Whether that panel is open, so the button reads as engaged like every other studio toggle. */
  localFilesOpen?: boolean;
  /** Cruise mode: tracks play one after another while engaged. Subtle, set-and-forget — lives in
   *  the header toolbar next to the count, not in the main bar. Absent = not offered. */
  cruise?: { enabled: boolean; onToggle: () => void } | null;
  /** "Load full sessions": while engaged, a drop replaces the deck with the card's original
   *  session instead of swapping only the card's own dimension. Absent = not offered. */
  loadDefaults?: { enabled: boolean; onToggle: () => void } | null;
  /** Where MIDI mappings are saved and loaded: on = this collection (per stage), off = the loaded orbiter. */
  midiScope?: { enabled: boolean; onToggle: () => void } | null;
  /** Entity filters (mixed collections only) — the header's LEFT toolbar: one square chip per card
   *  kind present, all on by default; turning one off hides that kind from the rail (view-only).
   *  Absent = no filters (homogeneous collection). */
  entityFilters?: Array<{
    key: string;
    icon: string;
    colorVar: string;
    label: string;
    enabled: boolean;
    onToggle: () => void;
  }> | null;
  /** Source (drawer) voiceIds with an in-flight drop/load — those cards show the orbit loader. */
  loadingSourceIds?: string[];
  /** Cards whose deck is up and which are now leaving the hand — turning face-down, then sliding
   *  out to the right. Still rendered; the studio drops them when the sequence ends. */
  exitingSourceIds?: string[];
  /** The offline-download control, when this surface offers one — a NODE rather than the feature's
   *  own props, so the drawer stays what it is: a rail of cards with a header toolbar. It renders in
   *  that toolbar beside the other collection-scoped switches, because a download is something you
   *  do to THIS collection. Absent = not offered (a queue, a room, a phone). */
  offlineControl?: React.ReactNode;
  /** Identity of the dealt HAND on the rail, when a room is dealing one. Changing it replays the
   *  deal animation: each card is remounted (the deal id is in its key), slides in from the
   *  drawer's edge and flips over from a face-down card back, staggered left to right. Null (the
   *  default) renders the rail exactly as before — no wrappers, no animation. */
  dealId?: string | null;
  /** The local audio rail (files on this machine), when this session offers one: the header's
   *  counters, the add-files control, the permission banner, the drop target and the local cards'
   *  own context menu all read from here. Null = not offered (a room, the queue, the flag off). */
  localRail?: LocalRailProps | null;
}

// Resizable-drawer clamp (desktop): between the minimum and the shorter of the 512px cap / the viewport.
function clampHeight(h: number): number {
  const viewportMax = typeof window === 'undefined' ? DESKTOP_MAX_HEIGHT : window.innerHeight - 48;
  return Math.min(Math.min(DESKTOP_MAX_HEIGHT, viewportMax), Math.max(MIN_HEIGHT, h));
}

/** The right-aligned drawer count. Entity-aware when the set is homogeneous ("3 audios"), else a generic
 *  "N cards" — a MIXED collection (tracks + orbiters) has no single noun, so it never says "N audios".
 *
 *  The plural comes from the TRANSLATION, not from this app adding an "s". Every language i18next
 *  knows has its own plural rule, and the ones that agreed with English here did so by coincidence. */
function countLabel(entries: DrawerEntry[]): string {
  const metas = entries.map((entry) => entityMeta(entry.entityType));
  // Homogeneous → that kind's own count. Mixed (or empty) → the generic one.
  const meta = new Set(metas.map((m) => m.countKey)).size === 1 ? metas[0] : entityMeta(null);
  return getT()(meta.countKey, { count: entries.length, defaultValue: `{{count}} ${meta.noun}s` }) as string;
}

/** The "open this entity's page" label, one WHOLE phrase per entity kind rather than a noun interpolated
 *  into a sentence — Spanish needs its own article and capitalisation per noun, which interpolation can't
 *  express. Written out (not built from the kind) so the keys stay greppable for translators. */
const OPEN_PAGE_KEY: Record<'track' | 'orbiter' | 'world', string> = {
  track: 'studio.menu.openTrack',
  orbiter: 'studio.menu.openOrbiter',
  world: 'studio.menu.openWorld',
};

/**
 * Right-click / long-press a card for the actions that belong to the ENTITY it stands for: open its own
 * page on the main site, and — for a viewer who may edit the collection — remove it from the collection.
 * Wraps the card via `asChild`, so it adds no DOM box and the drag handlers stay where they are.
 *
 * One menu covers both input models: Radix's ContextMenu opens on right-click AND on a touch long-press,
 * so the same markup gives desktop and mobile the same actions with no second surface to keep in step.
 *
 * With no action to offer (an entry with no page, and no removal available) the card gets NO menu at all,
 * so right-click stays native — which is what it did before removal existed.
 *
 * The link opens in a NEW TAB: the realm is a live audio session over one AudioContext, and navigating
 * away in place would tear it down mid-listen.
 */
function CardContextMenu({
  entry,
  onOpen,
  onRemove,
  removeLabelKey,
  children,
}: {
  entry: EntityRef & { title?: string | null };
  /** The menu opened — the parent drops any drag this same press armed (a touch long-press opens with
   *  no pointercancel, so the drag would otherwise still drop the card on release). */
  onOpen?: () => void;
  /** Remove this card from the collection. Absent = this viewer may not edit it (or there is no
   *  collection behind the rail), and the item is not rendered at all. */
  onRemove?: (() => void) | null;
  /** i18n key for the remove label — the card leaves a collection, or the user's queue. */
  removeLabelKey?: string;
  children: React.ReactNode;
}) {
  const url = entityPageUrl(entry);
  const meta = entityMeta(entry.entityType);
  // Both must hold to offer the OPEN action, and they always travel together — an entry with no known
  // kind has no site page either. Stated as one guard so neither is assumed from the other.
  const canOpen = Boolean(url && meta.kind);
  if (!canOpen && !onRemove) return <>{children}</>;
  const t = getT();

  return (
    <ContextMenu onOpenChange={(open) => open && onOpen?.()}>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="dark orb-studio__portal-surface">
        {/* The menu opens at the cursor, away from its card — without the title the action has no subject. */}
        <ContextMenuLabel>{entry.title || t('studio.menu.untitled')}</ContextMenuLabel>
        <ContextMenuSeparator />
        {canOpen ? (
          <ContextMenuItem onSelect={() => window.open(url as string, '_blank', 'noopener,noreferrer')}>
            <Icon name={meta.icon} style={{ width: 18, height: 18 }} aria-hidden />
            <span>{t(OPEN_PAGE_KEY[meta.kind as 'track' | 'orbiter' | 'world'])}</span>
            <SquareArrowOutUpRight style={{ marginLeft: 'auto', opacity: 0.6 }} />
          </ContextMenuItem>
        ) : null}
        {canOpen && onRemove ? <ContextMenuSeparator /> : null}
        {onRemove ? (
          /* The library's own destructive variant — it carries the tinted focus background as well as the
             red ink, which an inline colour would drop (leaving red text on the default accent fill).
             No confirm step: this is root's reading of the same action — the item leaves the collection,
             the entity itself is untouched, and a card put back is one search away. A modal here would
             also stop the music behind it, which removing a card should not do. */
          <ContextMenuItem variant="destructive" onSelect={() => onRemove()}>
            <FolderMinus style={{ width: 18, height: 18 }} aria-hidden />
            <span>{t(removeLabelKey ?? 'studio.menu.removeFromCollection')}</span>
          </ContextMenuItem>
        ) : null}
      </ContextMenuContent>
    </ContextMenu>
  );
}

/**
 * The context menu of a LOCAL card — the design's right-click on a missing card, and the smaller
 * set a reachable one gets. Same Radix `ContextMenu` as the hosted card's, so right-click and
 * long-press behave identically across the rail.
 */
function LocalCardMenu({
  entry,
  rail,
  onOpen,
  children,
}: {
  entry: DrawerEntry;
  rail: LocalRailProps;
  onOpen?: () => void;
  children: React.ReactNode;
}) {
  const t = getT();
  const status = entry.local?.status ?? 'ready';
  const missing = status === 'missing';
  const act = (action: LocalCardAction) => () => rail.onAction(entry.voiceId, action);
  const iconStyle = { width: 18, height: 18 } as const;
  return (
    <ContextMenu onOpenChange={(open) => open && onOpen?.()}>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="dark orb-studio__portal-surface">
        <ContextMenuLabel>{entry.title || t('studio.menu.untitled')}</ContextMenuLabel>
        <ContextMenuSeparator />
        {missing ? (
          <>
            <ContextMenuItem onSelect={act('locate')}>
              <MapPin style={iconStyle} aria-hidden />
              <span>{t('studio.local.menu.locate')}</span>
            </ContextMenuItem>
            <ContextMenuItem onSelect={act('search-folder')}>
              <FolderSearch style={iconStyle} aria-hidden />
              <span>{t('studio.local.menu.searchFolder')}</span>
            </ContextMenuItem>
            <ContextMenuItem onSelect={act('show-path')}>
              <Route style={iconStyle} aria-hidden />
              <span>{t('studio.local.menu.showPath')}</span>
            </ContextMenuItem>
            <ContextMenuSeparator />
          </>
        ) : null}
        {status === 'deferred' || status === 'failed' ? (
          <ContextMenuItem onSelect={act('analyse-now')}>
            <Waves style={iconStyle} aria-hidden />
            <span>{t('studio.local.menu.analyseNow')}</span>
          </ContextMenuItem>
        ) : null}
        {/* A card that has already been analysed can be analysed AGAIN. Nothing re-runs on its own
            once a file is done, so when the analysis itself has learned something new — a tempo,
            where an older import stored none — asking for it by hand is the only way to get it. */}
        {status === 'ready' ? (
          <ContextMenuItem onSelect={act('reanalyse')}>
            <Waves style={iconStyle} aria-hidden />
            <span>{t('studio.local.menu.analyseAgain')}</span>
          </ContextMenuItem>
        ) : null}
        {!missing && (entry.local?.path || entry.local?.folder) ? (
          <ContextMenuItem onSelect={act('show-path')}>
            <Route style={iconStyle} aria-hidden />
            <span>{t('studio.local.menu.showPath')}</span>
          </ContextMenuItem>
        ) : null}
        {/* The same file can be in several collections. When it is, this takes it out of THIS one
            and leaves the bytes where the others can still play them — so the item says so rather
            than promising a removal that would not happen. */}
        <ContextMenuItem variant="destructive" onSelect={act('remove')}>
          <Trash2 style={iconStyle} aria-hidden />
          <span>{t(entry.local?.otherCollections ? 'studio.local.menu.removeFromCollection' : 'studio.local.menu.remove')}</span>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

/**
 * The rail header's local notices: what is missing, what needs permission, what is being analysed.
 *
 * Only things there is something to DO about. A running count of files and of bytes used to sit here
 * too; both were bookkeeping in a header whose job is playing music, and neither connected to
 * anything next to them.
 */
function LocalRailSummary({ rail }: { rail: LocalRailProps }) {
  const t = getT();
  if (!rail.analysing && rail.needPermission === 0 && rail.missing === 0) return null;
  const muted = 'var(--color-muted-foreground, rgba(255,255,255,0.6))';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: '0.75rem', color: muted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {rail.missing > 0 ? (
        <button
          type="button"
          onClick={() => rail.onSetMissingOpen(true)}
          style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer', color: 'var(--ps-entity-collection)', borderBottom: '1px solid var(--ps-entity-collection)', whiteSpace: 'nowrap' }}
        >
          {t('studio.local.missingCount', { count: rail.missing })}
        </button>
      ) : null}
      {rail.needPermission > 0 ? (
        /* The one case that needs a click (design 3b): a short line and a small button in the header,
           nothing that interrupts the rail. One click re-authorises every remembered file. */
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
          <LockKeyhole style={{ width: 11, height: 11, opacity: 0.6 }} aria-hidden />
          <span>{t('studio.local.permissionBanner', { count: rail.needPermission })}</span>
          <Button variant="link" size="sm" onClick={() => rail.onAllowAccess()} style={{ height: 'auto', padding: 0, fontSize: '0.75rem', color: 'var(--ps-entity-orbiter)' }}>
            {t('studio.local.allowAccess')}
          </Button>
        </span>
      ) : null}
      {/* Whatever was dropped is analysed in the background, and this line is the whole of what is
          said about it: how far along, and what is taking the machine's attention. Nothing to
          answer, nothing to dismiss — but somebody who notices the app working knows why. */}
      {rail.analysing ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: 'var(--ps-entity-world)', whiteSpace: 'nowrap' }}>
          <span style={{ position: 'relative', width: 52, height: 2, background: 'color-mix(in oklab, var(--color-foreground, #ffffff) 14%, transparent)' }}>
            <span
              style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${rail.analysing.total ? Math.round((rail.analysing.done / rail.analysing.total) * 100) : 0}%`,
                background: 'var(--ps-entity-world)', transition: 'width 300ms ease-out',
              }}
            />
          </span>
          {t('studio.local.analysing', { index: Math.min(rail.analysing.total, rail.analysing.done + 1), total: rail.analysing.total })}
        </span>
      ) : null}
    </div>
  );
}

export function CardDrawer({
  open,
  fixedHeight,
  initialHeight = null,
  onHeightSettled,
  entries,
  activeVoiceId = null,
  draggingVoiceId = null,
  dropBeforeVoiceId = null,
  dropAtEnd = false,
  onCardPointerDown,
  onCardMenuOpen,
  onRemoveEntry = null,
  removeEntryLabelKey = 'studio.menu.removeFromCollection',
  registerCardRef,
  registerEndRef,
  registerScrollRef,
  containerRef,
  onRequestClose,
  onOpenLocalFiles = null,
  localFilesOpen = false,
  rowsDraggingOver = false,
  cruise = null,
  loadDefaults = null,
  midiScope = null,
  entityFilters = null,
  offlineControl = null,
  loadingSourceIds,
  exitingSourceIds,
  dealId = null,
  localRail = null,
}: CardDrawerProps) {
  const t = getT();
  // OS files dragged over the rail: the rail is the designed drop target ("drop or picker onto the
  // rail"). Counted rather than flagged because dragenter/dragleave fire for every child crossed.
  const [fileDragDepth, setFileDragDepth] = useState(0);
  // Highlighted for BOTH ways a file can arrive: dragged in from the operating system, and dragged
  // out of the local-files panel. One outline, because they mean the same thing to the person doing
  // it — and a second one drawn slightly differently would be a second thing to keep in step.
  const fileDragging = Boolean(localRail) && (fileDragDepth > 0 || rowsDraggingOver);
  const onFileDragEnter = (e: React.DragEvent) => {
    if (!localRail || !dragCarriesFiles(e.dataTransfer)) return;
    e.preventDefault();
    setFileDragDepth((d) => d + 1);
  };
  const onFileDragOver = (e: React.DragEvent) => {
    if (!localRail || !dragCarriesFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onFileDragLeave = (e: React.DragEvent) => {
    if (!localRail || !dragCarriesFiles(e.dataTransfer)) return;
    setFileDragDepth((d) => Math.max(0, d - 1));
  };
  const onFileDrop = (e: React.DragEvent) => {
    if (!localRail || !dragCarriesFiles(e.dataTransfer)) return;
    e.preventDefault();
    setFileDragDepth(0);
    localRail.onDropFiles(e.dataTransfer, null);
  };
  // The global control-tooltips flag — the drawer's toggle drives the SAME system the orbiter
  // More menu does, so either surface flips assistance for the whole app.
  const tooltipsOn = useTooltipsEnabled();
  // Opened at the height this viewer last left, clamped into range — see `initialHeight`.
  const [height, setHeight] = useState(() =>
    Number.isFinite(initialHeight) ? clampHeight(initialHeight as number) : DEFAULT_HEIGHT,
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    move?: (e: PointerEvent) => void;
    up?: (e: PointerEvent) => void;
    cancel?: (e: PointerEvent) => void;
  }>({});
  const hasActiveSelection = activeVoiceId !== null;
  const resizable = fixedHeight == null;
  // Fixed (mobile): one reduced height, no resize. Otherwise the resizable state height (desktop).
  const effectiveHeight = fixedHeight ?? height;
  // The drawer's strip, published for whatever has to stop above it — today the local-files panel,
  // which runs from the drawer's top edge to the top of the studio. Taken from the height the
  // drawer is ABOUT to render at rather than measured back off the DOM: it is already the exact
  // number, and a measurement would not survive a hidden tab (no rendering steps, no observer).
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--orbiters-drawer-height', `${open ? effectiveHeight : 0}px`);
    return () => {
      root.style.setProperty('--orbiters-drawer-height', '0px');
    };
  }, [effectiveHeight, open]);

  // Card height (and thus its responsive tier) is derived from the drawer height — root's exact math.
  const contentHeight = Math.max(0, effectiveHeight - 12);
  const cardHeight = Math.max(MIN_CARD_HEIGHT, contentHeight - CARD_VERTICAL_CLEARANCE);
  const compact = cardHeight < COMPACT_CARD_BREAKPOINT;
  const reduced = cardHeight < REDUCED_CARD_BREAKPOINT;

  /** Take down whatever resize drag is in flight, without reporting anything. */
  const endResize = useCallback(() => {
    const drag = dragRef.current;
    if (drag.move) document.removeEventListener('pointermove', drag.move);
    if (drag.up) document.removeEventListener('pointerup', drag.up);
    if (drag.cancel) document.removeEventListener('pointercancel', drag.cancel);
    dragRef.current = {};
  }, []);

  useEffect(() => endResize, [endResize]);

  const handleResizePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      // One drag at a time — a second press before the first release would stack a second pair of
      // listeners, and either release would settle both.
      endResize();
      const pointerId = e.pointerId;
      const startY = e.clientY;
      const startHeight = height;
      // Where the drag actually ended: a pointerup lands in the same tick as the last pointermove, so
      // the state behind it may not have re-rendered yet.
      let settled = startHeight;
      let willClose = false;
      const onMove = (ev: PointerEvent) => {
        // Only the finger that started this drag resizes it.
        if (ev.pointerId !== pointerId) return;
        // Mobile (fixed height): the handle only CLOSES — drag the top border down past the threshold to
        // dismiss. There's no resize, so the drawer height never changes under the finger.
        if (!resizable) {
          willClose = ev.clientY - startY > CLOSE_THRESHOLD;
          return;
        }
        const next = startHeight + (startY - ev.clientY); // drag up grows
        if (next <= MIN_HEIGHT - CLOSE_THRESHOLD) {
          willClose = true;
          return;
        }
        willClose = false;
        settled = clampHeight(next);
        setHeight(settled);
      };
      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return; // another pointer's release is not this drag's end
        endResize();
        if (willClose) onRequestClose();
        // Where the handle came to rest. Not reported when the drag CLOSED the drawer — that gesture
        // is about showing the drawer or not, and the height it swept past on its way down is not a
        // height anybody chose to leave it at.
        else if (resizable && settled !== startHeight) onHeightSettled?.(settled);
      };
      // Cancelled (the browser claimed the pointer): the handle was never let go of, so nothing is
      // reported — the drawer simply stays the size it currently shows.
      const onCancel = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        endResize();
      };
      dragRef.current = { move: onMove, up: onUp, cancel: onCancel };
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onCancel);
    },
    [endResize, height, onHeightSettled, onRequestClose, resizable],
  );

  const scrollByAmount = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = Math.max(220, Math.round(el.clientWidth * 0.72));
    el.scrollBy({ left: direction === 'left' ? -delta : delta, behavior: 'smooth' });
  }, []);

  // Does the rail actually overflow? A dealt hand (or any small collection) usually fits, and then
  // the cards sit centred with no scroll arrows — the arrows are a remedy for overflow, not chrome.
  //
  // COMPUTED from the card count, never measured from scrollWidth: mid-deal the flip animation
  // translates every card sideways, transformed descendants extend scrollWidth, and a measurement
  // taken while cards fly in reads wider than the settled rail — and sticks, because nothing
  // re-measures after the animation. Card width is a constant, so arithmetic is exact and immune.
  const [railOverflows, setRailOverflows] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const count = entries.length;
      // n cards + the 40px end drop-zone, with the rail's 16px gap between every pair.
      const contentWidth = count > 0 ? count * CARD_WIDTH + 40 + RAIL_GAP * count : 0;
      setRailOverflows(contentWidth > el.clientWidth + 1);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [entries.length, open, fixedHeight]);

  return (
    <div
      ref={containerRef}
      style={{ ...drawerStyle, height: open ? effectiveHeight : 0, outline: fileDragging ? '2px dashed var(--color-foreground, #ffffff)' : undefined, outlineOffset: -4 }}
      aria-hidden={!open}
      onDragEnter={onFileDragEnter}
      onDragOver={onFileDragOver}
      onDragLeave={onFileDragLeave}
      onDrop={onFileDrop}
    >
      {/* Top-border handle. Desktop (resizable): drag up/down to resize, far down to close. Mobile (fixed
          height): drag down to close — the same gesture, minus the resize (the Cards toggle also opens/closes).
          The grab bar is always here so the drawer can be dismissed by dragging its top border. */}
      <button
        type="button"
        data-orbiter-drawer-resize=""
        onPointerDown={handleResizePointerDown}
        aria-label={resizable ? t('studio.resizeDrawer', { defaultValue: 'Resize drawer' }) : t('studio.closeCards', { defaultValue: 'Close cards' })}
        style={resizeHandleStyle}
      >
        <span style={{ width: 36, height: 3, borderRadius: 9999, background: 'color-mix(in oklab, var(--color-foreground, #ffffff) 30%, transparent)' }} />
      </button>

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '0 16px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
          {/* LEFT toolbar — entity filters (mixed collections): square chips, one per card kind,
              tinted with the entity accent while shown; dimmed when that kind is hidden. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexShrink: 1, overflow: 'hidden' }}>
            {entityFilters?.map((filter) => (
              <Button
                key={filter.key}
                variant="ghost"
                size="icon"
                onClick={() => toggleThenRefreshTooltips(filter.onToggle)}
                aria-pressed={filter.enabled}
                aria-label={t(filter.enabled ? 'studio.filters.shown' : 'studio.filters.hidden', {
                  label: t(`studio.filters.${filter.key}`, { defaultValue: filter.label }),
                })}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: filter.enabled
                    ? `color-mix(in oklab, var(${filter.colorVar}) 18%, transparent)`
                    : 'transparent',
                  boxShadow: filter.enabled
                    ? `0 0 0 1px color-mix(in oklab, var(${filter.colorVar}) 30%, transparent)`
                    : '0 0 0 1px color-mix(in oklab, var(--color-foreground, #ffffff) 15%, transparent)',
                  color: filter.enabled ? `var(${filter.colorVar})` : 'var(--color-foreground, #ffffff)',
                  opacity: filter.enabled ? 1 : 0.35,
                }}
              >
                <Icon name={filter.icon} style={{ width: 15, height: 15 }} aria-hidden />
              </Button>
            ))}
            {localRail ? <LocalRailSummary rail={localRail} /> : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {localRail?.enabled ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => localRail.onPickFiles(null)}
              aria-label={t('studio.local.addFiles')}
              style={{ width: 28, height: 28, borderRadius: 9999, color: 'var(--color-foreground, #ffffff)', opacity: 0.75 }}
            >
              <FilePlus style={{ width: 16, height: 16 }} />
            </Button>
          ) : null}
          {/* Beside "add files", because it answers the question adding them raises: what is this
              machine now holding, and how do I get the space back. Adding is the rail's business;
              managing is the panel's, so this only opens it. */}
          {localRail?.enabled && onOpenLocalFiles ? (
            <Button
              data-orbiter-local-files-toggle=""
              variant="ghost"
              size="icon"
              onClick={onOpenLocalFiles}
              aria-pressed={localFilesOpen}
              aria-label={t('studio.local.manageFiles')}
              style={{
                width: 28,
                height: 28,
                borderRadius: 9999,
                background: localFilesOpen
                  ? 'color-mix(in oklab, var(--color-foreground, #ffffff) 18%, transparent)'
                  : 'transparent',
                color: 'var(--color-foreground, #ffffff)',
                opacity: localFilesOpen ? 1 : 0.75,
              }}
            >
              <HardDrive style={{ width: 16, height: 16 }} />
            </Button>
          ) : null}
          {offlineControl}
          {cruise && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toggleThenRefreshTooltips(cruise.onToggle)}
              aria-pressed={cruise.enabled}
              aria-label={t(cruise.enabled ? 'studio.cruiseOn' : 'studio.cruiseOff')}
              // Theme vars in the drawer chrome can resolve both states to the same
              // white — state reads through opacity + an engaged pill, not color vars.
              style={{
                width: 28,
                height: 28,
                borderRadius: 9999,
                background: cruise.enabled
                  ? 'color-mix(in oklab, var(--color-foreground, #ffffff) 18%, transparent)'
                  : 'transparent',
                color: 'var(--color-foreground, #ffffff)',
                opacity: cruise.enabled ? 1 : 0.35,
              }}
            >
              <ShipWheel style={{ width: 16, height: 16 }} />
            </Button>
          )}
          {loadDefaults && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toggleThenRefreshTooltips(loadDefaults.onToggle)}
              aria-pressed={loadDefaults.enabled}
              aria-label={t(loadDefaults.enabled ? 'studio.fullSessionsOn' : 'studio.fullSessionsOff')}
              style={{
                width: 28,
                height: 28,
                borderRadius: 9999,
                background: loadDefaults.enabled
                  ? 'color-mix(in oklab, var(--color-foreground, #ffffff) 18%, transparent)'
                  : 'transparent',
                color: 'var(--color-foreground, #ffffff)',
                opacity: loadDefaults.enabled ? 1 : 0.35,
              }}
            >
              <Layers style={{ width: 16, height: 16 }} />
            </Button>
          )}
          {midiScope && (
            <Button
              variant="ghost"
              size="icon"
              className="orb-studio__midi-scope-toggle"
              // Shown ONLY in learn mode (CSS, `body.midi-learn-mode`) — a stray press outside it
              // would move where every stage control saves its mapping, mid-performance.
              // The icon carries the scope's own colour, the same green/blue as the overlays and
              // CH/CC badges it governs, so pressing it visibly flips them together.
              data-midi-scope={midiScope.enabled ? 'collection' : 'orbiter'}
              onClick={() => toggleThenRefreshTooltips(midiScope.onToggle)}
              aria-pressed={midiScope.enabled}
              aria-label={t(midiScope.enabled ? 'studio.midiScopeCollection' : 'studio.midiScopeOrbiter')}
              style={{
                width: 28,
                height: 28,
                borderRadius: 9999,
                background: 'color-mix(in oklab, var(--color-foreground, #ffffff) 18%, transparent)',
              }}
            >
              <SlidersHorizontal style={{ width: 16, height: 16 }} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => toggleThenRefreshTooltips(() => toggleControlTooltips())}
            aria-pressed={tooltipsOn}
            aria-label={t(tooltipsOn ? 'studio.tooltipsOn' : 'studio.tooltipsOff')}
            style={{
              width: 28,
              height: 28,
              borderRadius: 9999,
              background: tooltipsOn
                ? 'color-mix(in oklab, var(--color-foreground, #ffffff) 18%, transparent)'
                : 'transparent',
              color: 'var(--color-foreground, #ffffff)',
              opacity: tooltipsOn ? 1 : 0.35,
            }}
          >
            {/* The platform's tooltip glyph (the "i") — the same icon the orbiter More menu uses. */}
            <Icon name="tooltip" style={{ width: 16, height: 16 }} aria-hidden />
          </Button>
          <span style={{ fontSize: '0.875rem', color: 'var(--color-muted-foreground, rgba(255,255,255,0.6))' }}>{countLabel(entries)}</span>
          </div>
        </div>

        <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          {localRail && fileDragging ? (
            <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 3, display: 'grid', placeItems: 'center', pointerEvents: 'none', fontSize: '0.9rem', color: 'var(--color-foreground, #ffffff)', background: 'color-mix(in oklab, var(--color-background, #000000) 60%, transparent)' }}>
              {t('studio.local.dropHint')}
            </div>
          ) : null}
          {entries.length > 0 && railOverflows && (
            <>
              <Button variant="ghost" size="icon" onClick={() => scrollByAmount('left')} aria-label={t('studio.scrollLeft', { defaultValue: 'Scroll left' })} style={{ ...arrowStyle, left: 0 }}>
                <ChevronLeft />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => scrollByAmount('right')} aria-label={t('studio.scrollRight', { defaultValue: 'Scroll right' })} style={{ ...arrowStyle, right: 0 }}>
                <ChevronRight />
              </Button>
            </>
          )}

          <div
            ref={(node) => {
              // No measuring here: an inline ref runs on EVERY render (detach + attach), so a
              // setState inside it is a render loop. The effect + ResizeObserver own the measurement.
              scrollRef.current = node;
              registerScrollRef(node);
            }}
            // Centring only when everything fits: with overflow, flex centring would clip the left
            // end of the rail behind an unreachable scroll edge.
            style={railOverflows ? railStyle : { ...railStyle, justifyContent: 'center' }}
          >
            {entries.map((entry, index) => {
              const isActive = entry.voiceId === activeVoiceId;
              const isDragging = entry.voiceId === draggingVoiceId;
              const isDropBefore = entry.voiceId === dropBeforeVoiceId;
              const card = (
                <StationTrackCard
                  entry={entry}
                  dragging={isDragging}
                  compact={compact}
                  reduced={reduced}
                  active={isActive}
                  dimmed={!isDragging && hasActiveSelection && !isActive}
                  showSubtitle={!reduced}
                  loading={loadingSourceIds?.includes(entry.voiceId) ?? false}
                />
              );
              const cardKey = dealId ? `${dealId}:${entry.voiceId}` : entry.voiceId;
              const cardBox = (
                <div
                  ref={(node: HTMLDivElement | null) => registerCardRef(entry.voiceId, node)}
                  onPointerDown={(e: ReactPointerEvent) => onCardPointerDown(entry, e)}
                  className={
                    dealId
                      ? `orb-deal ${exitingSourceIds?.includes(entry.voiceId) ? 'orb-deal--out' : 'orb-deal--in'}`
                      : undefined
                  }
                  style={{
                    position: 'relative',
                    flexShrink: 0,
                    width: CARD_WIDTH,
                    height: cardHeight,
                    borderRadius: 28,
                    // Every card lifts out — even one that can't seed an empty stage swaps into an
                    // occupied one — so every card advertises the grab.
                    cursor: 'grab',
                    userSelect: 'none',
                    // touch-action:none so the browser never claims a card touch for a native scroll — that
                    // pan would fire pointercancel and drop a card mid-drag. The parent owns BOTH the carousel
                    // scroll (horizontal swipe) and the drag (vertical pull) via one pointer session.
                    touchAction: 'none',
                    // Reorder insert-before hint — an accent ring on the target card (root's ring-2). Follows
                    // the chrome accent (`--color-border-2`); the light literal is the cold-start fallback.
                    boxShadow: isDropBefore ? '0 0 0 2px var(--color-border-2, rgba(255,255,255,0.6))' : undefined,
                    ...(dealId
                      ? ({
                          '--deal-h': `${cardHeight}px`,
                          '--deal-delay': `${index * 70}ms`,
                          '--deal-turn-ms': `${HAND_CARD_TURN_MS}ms`,
                          '--deal-hold-ms': `${HAND_CARD_HOLD_MS}ms`,
                          '--deal-slide-ms': `${HAND_CARD_SLIDE_MS}ms`,
                          '--deal-close-ms': `${HAND_CARD_CLOSE_MS}ms`,
                          '--deal-w': `${CARD_WIDTH}px`,
                          '--deal-gap': `${RAIL_GAP}px`,
                        } as CSSProperties)
                      : null),
                  }}
                >
                  {dealId ? (
                    <div className="orb-deal__inner">
                      <div className="orb-deal__front">{card}</div>
                      <div className="orb-deal__back" aria-hidden="true">
                        {/* The brand mark: the lib's FourCornerCard brackets (always shown — this is
                            the card's face, not a hover affordance) around the logo. Corner colour is
                            explicit because the back is deliberately always-dark, like a physical
                            card back, and must not follow the chrome theme's corner tokens. */}
                        <FourCornerCard
                          alwaysShowCorners
                          cornerColors="rgba(255, 255, 255, 0.35)"
                          className="orb-deal__back-corners"
                          contentClassName="orb-deal__back-content"
                        >
                          <img className="orb-deal__back-logo" src="/assets/card-back-logo.svg" alt="" draggable={false} />
                        </FourCornerCard>
                      </div>
                    </div>
                  ) : (
                    card
                  )}
                </div>
              );
              // A local card carries its own menu (locate / search a folder / show path / remove);
              // a hosted one keeps the entity menu. The deal id in the key is what replays the deal
              // animation: a new hand remounts every card, and the CSS animations run from their
              // face-down start.
              if (entry.local && localRail) {
                return (
                  <LocalCardMenu key={cardKey} entry={entry} rail={localRail} onOpen={onCardMenuOpen}>
                    {cardBox}
                  </LocalCardMenu>
                );
              }
              return (
                <CardContextMenu
                  key={cardKey}
                  entry={entry}
                  onOpen={onCardMenuOpen}
                  onRemove={onRemoveEntry && entry.removable ? () => onRemoveEntry(entry.voiceId) : null}
                  removeLabelKey={removeEntryLabelKey}
                >
                  {cardBox}
                </CardContextMenu>
              );
            })}

            {entries.length > 0 && (
              <div
                ref={registerEndRef}
                aria-hidden="true"
                style={{
                  flex: '0 0 auto',
                  width: 40,
                  alignSelf: 'stretch',
                  borderRadius: 20,
                  border: `1px dashed ${dropAtEnd ? 'var(--color-border-2, rgba(255,255,255,0.55))' : 'transparent'}`,
                  background: dropAtEnd ? 'color-mix(in oklab, var(--color-foreground, #ffffff) 6%, transparent)' : 'transparent',
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// The drawer is chrome — the parent attaches `applyStudioChromeTheme` via `containerRef`, so these
// `--color-*` tokens resolve to the USER chrome theme (surface + ink + font follow settings). Before the
// preset resolves the enclosing `.dark` scope supplies the light-on-dark defaults, so the `var(--x, literal)`
// literals are just last-ditch fallbacks. The frosted surface uses color-mix so the backdrop-blur still
// reads through.
const drawerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: 'color-mix(in oklab, var(--color-background, #000000) 50%, transparent)',
  backdropFilter: 'blur(10px)',
  pointerEvents: 'auto',
  flexShrink: 0,
  color: 'var(--color-foreground, #ffffff)',
  transition: 'height 360ms cubic-bezier(0.22,1,0.36,1)',
};

const resizeHandleStyle: React.CSSProperties = {
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'row-resize',
  touchAction: 'none',
  flexShrink: 0,
  border: 0,
  background: 'transparent',
};

const arrowStyle: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  borderRadius: 9999,
  background: 'color-mix(in oklab, var(--color-background, #000000) 55%, transparent)',
  color: 'var(--color-foreground, #ffffff)',
  backdropFilter: 'blur(4px)',
};

const railStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  gap: RAIL_GAP,
  height: '100%',
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  paddingBottom: 4,
  // The rail still scrolls via wheel / trackpad / the arrow buttons; touch scrolling is driven by the
  // parent's pointer session (cards are touch-action:none), so native touch panning is disabled here too
  // to keep one owner of the gesture.
  touchAction: 'none',
};
